"use client";

import React, { useLayoutEffect, useRef } from "react";
import type { ReactNode, CSSProperties } from "react";
import "./ScrollStack.css";

export interface ScrollStackItemProps {
  itemClassName?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export const ScrollStackItem: React.FC<ScrollStackItemProps> = ({
  children,
  itemClassName = "",
  style,
}) => (
  <div className={`scroll-stack-card ${itemClassName}`.trim()} style={style}>
    {children}
  </div>
);

interface ScrollStackProps {
  className?: string;
  children: ReactNode;
  /** Gap (px) between cards in normal flow. Controls scroll pacing. */
  itemDistance?: number;
  /** Offset (px) each stacked card peeks below the one in front of it. */
  itemStackDistance?: number;
  /** Smallest scale a fully-buried card recedes to (depth effect). */
  minScale?: number;
  /** Extra gap (px) between the sticky header and the pinned cards. */
  headerGap?: number;
  /** Selector (within the closest <section>) of a sticky header to pin beneath. */
  headerSelector?: string;
  /** Fallback pin offset (px) from the top when the header isn't sticky (e.g. mobile). */
  fallbackTop?: number;
  /** @deprecated retained for backwards compatibility — ignored. */
  useWindowScroll?: boolean;
  /** @deprecated retained for backwards compatibility — ignored. */
  startOnSectionEnter?: boolean;
}

/**
 * A scroll-driven card stack.
 *
 * Pinning is handled entirely by native `position: sticky`, so it stays smooth
 * under main-thread load and adapts to any viewport for free. The only JS is a
 * cheap, layout-read-free pass that scales already-stacked cards down a touch
 * for depth — decoupled from positioning so it can never cause positional jank.
 */
const ScrollStack: React.FC<ScrollStackProps> = ({
  children,
  className = "",
  itemDistance = 32,
  itemStackDistance = 14,
  minScale = 0.9,
  headerGap = 16,
  headerSelector = ".sticky-use-cases-header",
  fallbackTop = 92,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLElement[]>([]);
  const cardTopsRef = useRef<number[]>([]);
  const cardHeightsRef = useRef<number[]>([]);
  const stackTopRef = useRef(0);
  const peekRef = useRef(itemStackDistance);
  const minScaleRef = useRef(minScale);
  const lastScaleRef = useRef<number[]>([]);
  const progRef = useRef<number[]>([]);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef(false);
  const activeRef = useRef(true);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const cards = Array.from(
      scroller.querySelectorAll<HTMLElement>(".scroll-stack-card")
    );
    cardsRef.current = cards;
    lastScaleRef.current = new Array(cards.length).fill(1);
    progRef.current = new Array(Math.max(0, cards.length - 1)).fill(0);
    peekRef.current = itemStackDistance;
    minScaleRef.current = minScale;

    const section = scroller.closest("section");
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    scroller.style.setProperty("--peek", `${itemStackDistance}px`);
    scroller.style.setProperty("--card-gap", `${itemDistance}px`);
    cards.forEach((card, i) => {
      card.style.setProperty("--index", String(i));
      card.style.transformOrigin = "top center";
      card.style.backfaceVisibility = "hidden";
      if (!reduceMotion) card.style.willChange = "transform";
    });

    const docTop = (el: HTMLElement) => {
      let top = 0;
      let cur: HTMLElement | null = el;
      while (cur) {
        top += cur.offsetTop;
        cur = cur.offsetParent as HTMLElement | null;
      }
      return top;
    };

    const measure = () => {
      // Pin cards just beneath the sticky header; if the header isn't sticky
      // (it scrolls away on phones) pin them near the top of the viewport.
      let stackTop = fallbackTop;
      const header = section?.querySelector<HTMLElement>(headerSelector);
      if (header) {
        const cs = getComputedStyle(header);
        if (cs.position === "sticky") {
          stackTop = (parseFloat(cs.top) || 0) + header.offsetHeight + headerGap;
        }
      }
      stackTopRef.current = stackTop;
      scroller.style.setProperty("--stack-top", `${stackTop}px`);
      if (section) {
        section.style.setProperty("--stack-top", `${stackTop}px`);
        section.style.setProperty("--card-count", String(cards.length));
      }

      cardTopsRef.current = cards.map(docTop);
      cardHeightsRef.current = cards.map((c) => c.offsetHeight);
    };

    // Pure arithmetic from cached measurements — no per-frame layout reads.
    const update = () => {
      const n = cards.length;
      if (!n || !activeRef.current) return;
      const tops = cardTopsRef.current;
      const heights = cardHeightsRef.current;
      const stackTop = stackTopRef.current;
      const peek = peekRef.current;
      const min = minScaleRef.current;
      const step = n > 1 ? (1 - min) / (n - 1) : 0;
      const scrollY = window.scrollY;
      const prog = progRef.current;

      // prog[j] = how far card j+1 has slid over card j (0..1), eased.
      for (let j = 0; j < n - 1; j++) {
        const coverStart = tops[j + 1] - (stackTop + j * peek) - heights[j];
        const coverEnd = tops[j + 1] - (stackTop + (j + 1) * peek);
        let p =
          coverEnd > coverStart
            ? (scrollY - coverStart) / (coverEnd - coverStart)
            : scrollY >= coverEnd
              ? 1
              : 0;
        if (p < 0) p = 0;
        else if (p > 1) p = 1;
        prog[j] = p * p * (3 - 2 * p); // smoothstep
      }

      // depth_i = number of cards stacked on top of card i (continuous).
      let depthBelow = 0;
      for (let i = n - 1; i >= 0; i--) {
        const depth = (i <= n - 2 ? prog[i] : 0) + depthBelow;
        depthBelow = depth;
        let scale = 1 - depth * step;
        if (scale < min) scale = min;
        const rounded = Math.round(scale * 1000) / 1000;
        if (lastScaleRef.current[i] !== rounded) {
          cards[i].style.transform = `scale(${rounded})`;
          lastScaleRef.current[i] = rounded;
        }
      }
    };

    const requestUpdate = () => {
      if (pendingRef.current || reduceMotion) return;
      pendingRef.current = true;
      rafRef.current = requestAnimationFrame(() => {
        pendingRef.current = false;
        update();
      });
    };

    measure();
    if (!reduceMotion) update();

    const onScroll = () => requestUpdate();
    const onResize = () => {
      measure();
      requestUpdate();
    };

    // Only run scroll work while the stack is near the viewport.
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          activeRef.current = entries[0]?.isIntersecting ?? true;
          if (activeRef.current) requestUpdate();
        },
        { rootMargin: "100% 0px 100% 0px" }
      );
      io.observe(scroller);
    }

    // Re-measure once async layout settles (responsive height, images).
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            measure();
            requestUpdate();
          })
        : null;
    ro?.observe(scroller);

    // Fonts swap in after first paint and shift every offset — re-measure.
    if ("fonts" in document) {
      document.fonts.ready.then(() => {
        measure();
        requestUpdate();
      });
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("load", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onResize);
      io?.disconnect();
      ro?.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      pendingRef.current = false;
      cardsRef.current = [];
      cardTopsRef.current = [];
      cardHeightsRef.current = [];
      lastScaleRef.current = [];
      progRef.current = [];
    };
  }, [itemDistance, itemStackDistance, minScale, headerGap, headerSelector, fallbackTop]);

  return (
    <div
      ref={scrollerRef}
      className={`scroll-stack-scroller${className ? ` ${className}` : ""}`}
    >
      <div className="scroll-stack-inner">
        {children}
        <div className="scroll-stack-end" aria-hidden="true" />
      </div>
    </div>
  );
};

export default ScrollStack;
